import { Crepe } from '@milkdown/crepe';
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import { useCallback, useEffect, useId, useRef } from 'react';

import '@milkdown/crepe/theme/common/style.css';
import '@milkdown/crepe/theme/frame.css';
import './deprecated-milkdown.css';
import { Paper, PaperProps, Stack, Text, Flex } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import {
  replaceImagesWithPlaceholders,
  replacePlaceholdersWithImages,
} from 'src/common/components/markdown-editor/utils';

type EditorProps = {
  defaultValue?: string;
  onChange?: (value: string) => void;
  label?: string | React.ReactNode;
  headerRightComponent?: React.ReactNode;
  readonly?: boolean;
  placeholderImages?: MarkdownImage[];
} & PaperProps;

export default function DeprecatedMarkdownEditor({
  defaultValue,
  onChange = () => {},
  label,
  headerRightComponent,
  readonly = false,
  ...paperProps
}: EditorProps) {
  if (label) {
    return (
      <Stack gap="xs" h="100%">
        {label && (
          <Flex align="center" justify="space-between">
            {typeof label === 'string' ? (
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.8">
                {label}
              </Text>
            ) : (
              label
            )}
            {headerRightComponent}
          </Flex>
        )}
        <MilkdownProvider>
          <CrepeEditor
            {...paperProps}
            defaultValue={defaultValue}
            onChange={onChange}
            readonly={readonly}
          />
        </MilkdownProvider>
      </Stack>
    );
  }

  const id = useId();
  const key = readonly ? `readonly-${id}` : id;

  return (
    <MilkdownProvider>
      <div key={key}>
        <CrepeEditor
          {...paperProps}
          defaultValue={defaultValue}
          onChange={onChange}
          readonly={readonly}
        />
      </div>
    </MilkdownProvider>
  );
}

function CrepeEditor({
  defaultValue,
  onChange,
  placeholderImages = [],
  readonly = false,
  ...props
}: Omit<EditorProps, 'label' | 'headerRightComponent'>) {
  const crepeRef = useRef<Crepe | null>(null);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const imagesRef = useRef(placeholderImages);
  useEffect(() => {
    imagesRef.current = placeholderImages;
  }, [placeholderImages]);

  const applyImageEmbeds = useCallback(replacePlaceholdersWithImages, []);

  const toPlaceholders = useCallback(replaceImagesWithPlaceholders, []);

  useEditor(root => {
    const startMarkdown = applyImageEmbeds(defaultValue ?? '', imagesRef.current);

    const crepe = new Crepe({
      root,
      defaultValue: startMarkdown,
      features: {
        [Crepe.Feature.BlockEdit]: false,
      },
    });

    crepe.on(listener => {
      listener.markdownUpdated(() => {
        const raw = crepe.getMarkdown();
        const withPlaceholders = toPlaceholders(raw, imagesRef.current);
        onChangeRef.current?.(withPlaceholders);
      });
    });

    // Set readonly immediately during initialization
    if (readonly) {
      crepe.setReadonly(true);
    }

    crepeRef.current = crepe;
    return crepe;
  }, []);

  useEffect(() => {
    crepeRef.current?.setReadonly(readonly);
  }, [readonly]);

  return (
    <Paper
      variant="content"
      style={{ overflowY: 'auto' }}
      bg={readonly ? 'gray.1' : undefined}
      {...props}
    >
      <Milkdown />
    </Paper>
  );
}
